const _ = require("lodash");
const ErrorUtil = require("../../../shared/ErrorUtil");

/**
 * A small class that abstracts the system's speech API. Only this file should
 * directly interact with those APIs.
 */

/**
 * Internal helper for diagnostic purposes only. Given a ReadAloudContext,
 * returns a simple JSON object suitable for sending to ErrorUtil.
 *
 * @param {ReadAloudSynth} inst
 * @returns {object}
 */
const _getInstanceForLogging = function(inst) {
	return {
		available: inst.available,
		voiceName: inst.voiceName,
		hasSynthVoice: !!inst.synthVoice,
	};
};

/**
 * Constructor.
 *
 * By default, no voice is set. Until setVoice is called, speak is a noop.
 *
 * ---
 *
 * Was getting "Declaration emit for this file requires using private name".
 * This `type` annotation is a workaround. See: https://stackoverflow.com/questions/60009092/declaration-will-not-emit-due-to-private-name-usage
 * @type {unknown}
 */
const ReadAloudSynth = function(voicesLoadedCB) {
	this.available =
		!!global.SpeechSynthesisUtterance && !!global.speechSynthesis;
	this.availableVoices = [];

	this.voicesLoadedCB = voicesLoadedCB || _.noop;
	this.playing = false;
	this.paused = false;
	this.lastUtterance = null;
	this.listeners = [];
	if (this.available) {
		this._voiceChangeListener = () => {
			// The first time this event is fired is for when
			// the voices are loaded
			if (_.isEmpty(this.availableVoices)) {
				this.availableVoices = global.speechSynthesis.getVoices();
				this.voicesLoadedCB && this.voicesLoadedCB(this.availableVoices);
			}
		};
		if (global.speechSynthesis.hasOwnProperty("onvoiceschanged")) {
			global.speechSynthesis.onvoiceschanged = this._voiceChangeListener;
		} else {
			setTimeout(this._voiceChangeListener, 0);
		}
	}
};

/**
 * Cleanup method. Memory is leaked if you forget about a ReadAloudContext
 * instance without calling this method.
 *
 * @returns {object}
 */
ReadAloudSynth.prototype.destroy = function() {
	if (this.available) {
		global.speechSynthesis.onvoiceschanged = null;
	}
};

/**
 *
 * @returns {boolean}
 */
ReadAloudSynth.prototype.isSpeaking = function() {
	return global.speechSynthesis && global.speechSynthesis.speaking;
};

/**
 *
 * @returns {boolean}
 */
ReadAloudSynth.prototype.isPaused = function() {
	return (
		this.available && global.speechSynthesis && global.speechSynthesis.paused
	);
};

/**
 *
 * @returns {boolean}
 */
ReadAloudSynth.prototype.isPending = function() {
	return (
		this.available && global.speechSynthesis && global.speechSynthesis.pending
	);
};

/**
 *
 */
ReadAloudSynth.prototype.cancel = function() {
	if (global.speechSynthesis) {
		global.speechSynthesis.cancel();
		this.updateStatus({playing: false, paused: false});
	}
};

/**
 *
 */
ReadAloudSynth.prototype.pause = function() {
	if (global.speechSynthesis) {
		global.speechSynthesis.pause();
		this.updateStatus({playing: false, paused: true});
	}
};

/**
 *
 */
ReadAloudSynth.prototype.resume = function() {
	if (global.speechSynthesis) {
		global.speechSynthesis.resume();
		this.updateStatus({playing: true, paused: false});
	}
};

/**
 * Registers a listener func for when playing value updates.
 *
 */
ReadAloudSynth.prototype.onStatusChange = function(listenerFunc) {
	this.listeners.push(listenerFunc);
};

/**
 * Internal helper for updating the internal playing value and
 * firing off the event listeners.
 *
 */
ReadAloudSynth.prototype.updateStatus = function(newStatus = {}) {
	const {playing = false, paused = false} = newStatus;
	if (this.playing === playing && this.paused === paused) return;
	this.playing = playing;
	this.paused = paused;
	_.each(this.listeners, listenerFunc => listenerFunc({playing, paused}));
};

// TODO: if we allow directly customizing things like pitch or speed, add those
// methods.

/**
 * This actually triggers audio in the speech API. Pass the text you want to
 * be spoken. This will noop if a voice has not been set with setVoice or if
 * a speech API implementation is unavailable. The return value will be false
 * if the function noop'd for any reason.
 *
 * It is an error to pass no text, though this method will log to ErrorUtil
 * instead of throwing.
 *
 * @param {string} text
 * @returns {boolean}
 */
ReadAloudSynth.prototype.speak = function(text) {
	if (!this.available) {
		// Browser doesn't support the API we're using.
		return false;
	}

	// If anything is paused, currently speaking, or pending, cancel it
	if (this.isPaused() || this.isSpeaking() || this.isPending()) {
		this.cancel();
	}
	if (!text) {
		ErrorUtil.log(
			"E_INVALID_SPEAK_CALL",
			"Called rac.speak with no text provided",
			_getInstanceForLogging(this)
		);
		return false;
	}

	// Below globals will exist since this.available is true.
	const u = new global.SpeechSynthesisUtterance(text);
	u.onend = () => this.updateStatus({playing: false, paused: false});
	// Safari is more likely to fire the "end" event if you save the utterance
	// to prevent garbage collection and fire the speak in a setTimeout.
	this.lastUtterance = u;
	setTimeout(() => {
		global.speechSynthesis.speak(u);
		this.updateStatus({playing: true, paused: false});
	}, 0);
	return true;
};

module.exports = ReadAloudSynth;
